/**
 * ChatMessage — Markdown renderer for Expert Chat assistant messages.
 *
 * Features:
 *   - ReactMarkdown with remark-gfm (GFM: tables, strikethrough, task lists)
 *   - Fenced code blocks with Prism syntax highlighting (oneDark theme)
 *   - Copy-to-clipboard button on code blocks (2s "Copied!" feedback)
 *   - Streaming performance: renders <pre> fallback for large content during streaming
 *   - Custom link renderer (target="_blank", rel="noopener noreferrer")
 *   - Table wrapper for horizontal scrolling on overflow
 *   - React.memo to prevent re-renders when new messages arrive
 *
 * Story v19-1-4: Markdown Rendering in Expert Chat
 */

import React, { useState, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import type { Components } from 'react-markdown';

// Streaming threshold: above this length, use <pre> fallback during active streaming
// to prevent excessive ReactMarkdown re-parses (400 words ≈ 2000 chars).
const STREAMING_FALLBACK_THRESHOLD = 2000;

interface ChatMessageProps {
  content: string;
  isStreaming?: boolean;
}

// ── Code block with copy button ──────────────────────────────────────────────

interface CodeBlockProps {
  language: string;
  code: string;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ language, code }) => {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => undefined);
  };

  return (
    <div className="relative group">
      <button
        onClick={copy}
        className="absolute top-2 right-2 z-10 px-2 py-0.5 text-xs rounded bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors opacity-0 group-hover:opacity-100"
        aria-label="Copy code"
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <SyntaxHighlighter
        language={language}
        style={oneDark}
        PreTag="div"
        customStyle={{ margin: 0, borderRadius: '0.375rem', fontSize: '0.8125rem' }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

// ── Custom ReactMarkdown component map ───────────────────────────────────────

const components: Components = {
  code(props) {
    const { children, className } = props;
    const match = /language-(\w+)/.exec(className || '');
    const code = String(children).replace(/\n$/, '');
    if (match) {
      return <CodeBlock language={match[1]} code={code} />;
    }
    // Inline code
    return (
      <code className="bg-gray-800 px-1 rounded text-sm font-mono text-teal-300">
        {children}
      </code>
    );
  },
  a({ href, children }) {
    // Strip javascript: URLs to prevent XSS via malicious markdown links
    const safeHref = href && !href.startsWith('javascript:') ? href : undefined;
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 underline hover:text-blue-300"
      >
        {children}
      </a>
    );
  },
  h1({ children }) {
    return <h1 className="text-xl font-bold mt-4 mb-2 text-slate-100">{children}</h1>;
  },
  h2({ children }) {
    return <h2 className="text-lg font-semibold mt-3 mb-2 text-slate-100">{children}</h2>;
  },
  h3({ children }) {
    return <h3 className="text-base font-semibold mt-2 mb-1 text-slate-200">{children}</h3>;
  },
  p({ children }) {
    return <p className="my-1.5 leading-relaxed text-slate-200">{children}</p>;
  },
  li({ children }) {
    return <li className="leading-relaxed">{children}</li>;
  },
  ul({ children }) {
    return <ul className="list-disc list-inside my-2 space-y-1 text-slate-200">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="list-decimal list-inside my-2 space-y-1 text-slate-200">{children}</ol>;
  },
  blockquote({ children }) {
    return (
      <blockquote className="border-l-4 border-teal-500 pl-4 my-2 italic text-slate-300">
        {children}
      </blockquote>
    );
  },
  strong({ children }) {
    return <strong className="font-semibold text-slate-100">{children}</strong>;
  },
  em({ children }) {
    return <em className="italic text-slate-200">{children}</em>;
  },
  table({ children }) {
    return (
      <div className="overflow-x-auto my-2">
        <table className="border-collapse w-full text-sm">{children}</table>
      </div>
    );
  },
  thead({ children }) {
    return <thead className="bg-slate-700">{children}</thead>;
  },
  tr({ children }) {
    return <tr className="border-b border-slate-600 even:bg-slate-800/50">{children}</tr>;
  },
  th({ children }) {
    return <th className="px-3 py-2 text-left font-semibold text-slate-200 border border-slate-600">{children}</th>;
  },
  td({ children }) {
    return <td className="px-3 py-2 text-slate-300 border border-slate-600">{children}</td>;
  },
};

// ── ChatMessage ───────────────────────────────────────────────────────────────

// Blinking cursor shown at end of streaming content
const StreamingCursor = () => (
  <span className="inline-block w-0.5 h-4 bg-teal-400 animate-pulse ml-0.5 align-middle" />
);

const ChatMessage: React.FC<ChatMessageProps> = memo(({ content, isStreaming = false }) => {
  // Streaming performance: large content during active streaming → use <pre> fallback
  // to avoid excessive ReactMarkdown re-parses and layout thrash.
  // Switches to full ReactMarkdown rendering when streaming ends.
  if (isStreaming && content.length > STREAMING_FALLBACK_THRESHOLD) {
    return (
      <pre className="whitespace-pre-wrap text-sm text-slate-200 font-sans">
        {content}
        <StreamingCursor />
      </pre>
    );
  }

  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
      {isStreaming && <StreamingCursor />}
    </>
  );
});

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
